"use client";

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  label?: string;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, componentStack: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[ErrorBoundary${this.props.label ? `: ${this.props.label}` : ""}]`, error, info);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  render() {
    if (!this.state.error) return this.props.children;

    const { error, componentStack } = this.state;

    return (
      <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-3 bg-red-50 p-6 text-center">
        <p className="text-2xl" aria-hidden>
          🛠️
        </p>
        <h2 className="text-lg font-bold text-red-900">
          {this.props.label ? `${this.props.label} crashed` : "Something went wrong"}
        </h2>
        <p className="max-w-md text-sm text-red-800">{error.message}</p>
        <pre className="max-h-48 w-full max-w-lg overflow-auto rounded-lg bg-red-100 p-3 text-left text-xs text-red-950">
          {error.stack}
          {componentStack ? `\n\nComponent stack:${componentStack}` : ""}
        </pre>
        <button
          type="button"
          onClick={() => this.setState({ error: null, componentStack: null })}
          className="rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white"
        >
          Try again
        </button>
      </div>
    );
  }
}
